import type { DoseMatrix } from '~/domain/patient/doseMatrix';
import type { Image3D } from '~/domain/patient/image3d';
import type { RigidRegistration } from '~/domain/patient/rigidRegistration';
import type { StructureSet } from '~/domain/patient/structureset';

/**
 * The datasets the viewer works on: the primary image, the structure set
 * drawn on it, the dose of that set, and the image fused onto the primary
 * with the registration that places it. A field left out is not selected.
 */
export interface DatasetSelection {
    readonly primaryImage?: Image3D;
    readonly structureSet?: StructureSet;
    readonly dose?: DoseMatrix;
    readonly secondaryImage?: Image3D;
    readonly registration?: RigidRegistration;
}

/** A dataset the user asks to work on. */
export type SelectionRequest =
    | { readonly kind: 'primaryImage'; readonly image: Image3D }
    | { readonly kind: 'structureSet'; readonly structureSet: StructureSet }
    | { readonly kind: 'dose'; readonly dose: DoseMatrix };

/** Why a request cannot be met. Nothing is selected in its place. */
export type SelectionRefusal =
    | {
          readonly reason: 'structureSetWithoutImage';
          readonly structureSet: StructureSet;
      }
    | { readonly reason: 'doseWithoutStructureSet' }
    | { readonly reason: 'doseWithoutImage' };

export type SelectionOutcome =
    /** The request names what is already selected. */
    | { readonly kind: 'unchanged' }
    | { readonly kind: 'refused'; readonly refusal: SelectionRefusal }
    | {
          readonly kind: 'selected';
          readonly selection: DatasetSelection;
          /** The structure set differs from the current one. */
          readonly structureSetChanged: boolean;
      };

/**
 * The selection that meets `request` from `current`, or why it cannot be
 * met. The rules:
 *
 * - A primary image is selected alone: the structure set, the dose, the
 *   fused image and its registration belong to the image that leaves.
 *   Asking for the current primary changes nothing.
 * - A structure set is drawn on its own image, so that image becomes the
 *   primary. On the same primary the fused image and its registration
 *   stay; on another they go. The dose goes either way: a dose is chosen
 *   after its set. A set with no image is refused.
 * - A dose is drawn with the structure set it references, so that set is
 *   selected first, by the structure set rule, and the dose joins it. A
 *   dose with no set, or whose set has no image, is refused.
 */
export function selectDataset(
    current: DatasetSelection,
    request: SelectionRequest
): SelectionOutcome {
    switch (request.kind) {
        case 'primaryImage':
            if (request.image === current.primaryImage) {
                return { kind: 'unchanged' };
            }
            return {
                kind: 'selected',
                selection: { primaryImage: request.image },
                structureSetChanged: current.structureSet !== undefined,
            };
        case 'structureSet':
            return selectStructureSet(current, request.structureSet);
        case 'dose': {
            const { dose } = request;
            if (!dose.structureSet) {
                return refused({ reason: 'doseWithoutStructureSet' });
            }
            if (!dose.structureSet.image) {
                return refused({ reason: 'doseWithoutImage' });
            }
            if (dose.structureSet === current.structureSet) {
                return {
                    kind: 'selected',
                    selection: { ...current, dose },
                    structureSetChanged: false,
                };
            }
            const outcome = selectStructureSet(current, dose.structureSet);
            return outcome.kind === 'selected'
                ? {
                      ...outcome,
                      selection: { ...outcome.selection, dose },
                  }
                : outcome;
        }
    }
}

function selectStructureSet(
    current: DatasetSelection,
    structureSet: StructureSet
): SelectionOutcome {
    const image = structureSet.image;
    if (!image) {
        return refused({ reason: 'structureSetWithoutImage', structureSet });
    }
    return {
        kind: 'selected',
        selection:
            image !== current.primaryImage
                ? { primaryImage: image, structureSet }
                : { ...current, structureSet, dose: undefined },
        structureSetChanged: structureSet !== current.structureSet,
    };
}

function refused(refusal: SelectionRefusal): SelectionOutcome {
    return { kind: 'refused', refusal };
}
